/**
 * The TV page's sound effects (Web Audio). The phone hands sound to a TV
 * that advertises `tv.sfx.v1` and then stays silent itself
 * (app/LagKing/SOUNDS.md). Everything here follows the phone's `sound`
 * snapshot field: no sound unless the phone says the TV owns it and the
 * player turned Sound on.
 *
 * - Cup drop: derived here from the putt event's holed flag, timed to this
 *   page's own replay (SceneContent onBallHoled), never sent by the phone.
 * - Moments (clap, cheer, roar, groan): the `sound.moment` flag in the state
 *   publish. A 'putt' moment plays at the putt's drop when it outranks the
 *   cup drop, or when the ball stops; a 'round' moment plays as it arrives.
 *   A make of 30 ft+ in the main cup roars, derived here like the cup drop.
 * - Miss tone: the putt event's `sfx: 'miss'`, played as the roll starts.
 *
 * Browsers start audio suspended until the viewer interacts with the page,
 * so the first click or key press (the remote's OK) unlocks it; SoundUnlock
 * shows a one-line prompt while sound is on but still locked.
 *
 * The decisions (who owns sound, which sound a putt gets) live in
 * tvSfxController.ts, shared with the Apple TV app (web/tv/appletv/src/sfx.ts).
 *
 * Nothing here throws: a failure is logged once and the page stays quiet.
 */
import { SFX_FADE_IN_MS, SFX_FADE_OUT_MS, SFX_FILES, SFX_OVERLAY, SFX_VOLUME, type SfxName } from '../../../packages/putt-scene/src/sound';
import { TvSfxController } from './tvSfxController';
import { sideViewFrom } from './sideView';

export { TvSfxController };

type Listener = () => void;

const warned = new Set<string>();
function warnOnce(what: string, error: unknown): void {
  if (warned.has(what)) return;
  warned.add(what);
  console.warn(`[tv sfx] ${what} failed; sounds stay quiet`, error);
}

// ---- Web Audio -------------------------------------------------------------

let context: AudioContext | null = null;
const buffers: Partial<Record<SfxName, AudioBuffer>> = {};
let loading: Promise<void> | null = null;
let current: { source: AudioBufferSourceNode; gain: GainNode; name: SfxName } | null = null;
const listeners = new Set<Listener>();

function audioContext(): AudioContext | null {
  if (context) return context;
  try {
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    context = new Ctor();
    context.onstatechange = () => listeners.forEach((listener) => listener());
    return context;
  } catch (error) {
    warnOnce('audio context', error);
    return null;
  }
}

/** Where the sound files are served: relative, like every asset on this
 *  page. The app's browser build (play.lagking.com) routes paths such as
 *  /Challenge, so it serves them from the root instead (setSfxBase). */
let sfxBase = './sfx/';

/** Set before the first sound loads. */
export function setSfxBase(base: string): void {
  sfxBase = base.endsWith('/') ? base : `${base}/`;
}

function load(): Promise<void> {
  if (loading) return loading;
  const ctx = audioContext();
  if (!ctx) return Promise.resolve();
  loading = Promise.all(
    (Object.keys(SFX_FILES) as SfxName[]).map(async (name) => {
      try {
        // Served from public/sfx (sfxBase).
        const response = await fetch(`${sfxBase}${SFX_FILES[name]}`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        buffers[name] = await ctx.decodeAudioData(await response.arrayBuffer());
      } catch (error) {
        warnOnce(`load ${name}`, error);
      }
    }),
  ).then(() => undefined);
  return loading;
}

/** Side view (sideView.ts) is silent (Cole, 2026-10-21: the game next to it
 *  has its own sound). The page still advertises sound, so the phone stays
 *  quiet too. */
const silent = typeof window !== 'undefined' && sideViewFrom(window.location.search) != null;

function playBuffer(name: SfxName, seekMs = 0): void {
  if (silent) return;
  try {
    const ctx = audioContext();
    if (!ctx || ctx.state !== 'running') return;
    const buffer = buffers[name];
    if (!buffer) {
      void load();
      return;
    }
    // One at a time: a new sound stops the one playing. The cup drop is an
    // overlay (SFX_OVERLAY): it plays on top, and is never the one stopped.
    // The build fades out under the next sound and the groan-release eases
    // in (SFX_FADE_*), so the two crossfade instead of cutting.
    const overlay = SFX_OVERLAY.has(name);
    const now = ctx.currentTime;
    if (!overlay && current) {
      const cutting = current;
      const fadeOut = (SFX_FADE_OUT_MS[cutting.name] ?? 0) / 1000;
      try {
        if (fadeOut > 0) {
          cutting.gain.gain.cancelScheduledValues(now);
          cutting.gain.gain.setValueAtTime(cutting.gain.gain.value, now);
          cutting.gain.gain.linearRampToValueAtTime(0, now + fadeOut);
          cutting.source.stop(now + fadeOut);
        } else {
          cutting.source.stop();
        }
      } catch {
        // already ended
      }
    }
    const source = ctx.createBufferSource();
    const gain = ctx.createGain();
    const fadeIn = (SFX_FADE_IN_MS[name] ?? 0) / 1000;
    if (fadeIn > 0) {
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(SFX_VOLUME[name], now + fadeIn);
    } else {
      gain.gain.value = SFX_VOLUME[name];
    }
    source.buffer = buffer;
    source.connect(gain).connect(ctx.destination);
    const playingNow = { source, gain, name };
    source.onended = () => {
      if (current === playingNow) current = null;
    };
    if (!overlay) current = playingNow;
    // A build started late seeks in so its crest still lands at the cup.
    source.start(0, Math.max(0, Math.min(seekMs / 1000, buffer.duration - 0.01)));
  } catch (error) {
    warnOnce(`play ${name}`, error);
  }
}

export const tvSfx = new TvSfxController(playBuffer);

/** Whether the browser is still holding audio until an interaction. */
export function soundLocked(): boolean {
  if (silent) return false;
  const ctx = context;
  return !ctx || ctx.state !== 'running';
}

/** Resume audio after a click or key press (browser autoplay rules). */
export function unlockSound(): void {
  const ctx = audioContext();
  if (!ctx) return;
  void load();
  if (ctx.state === 'running') return;
  ctx.resume().then(
    () => listeners.forEach((listener) => listener()),
    (error) => warnOnce('resume', error),
  );
}

export function subscribeSound(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
